import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type UseInfiniteQueryOptions,
} from 'react-query';
import * as yup from 'yup';
import { baseFetch } from '../api/baseFetch';
import {
  OrderSummarySchema,
  type OrderSummaryResponse,
} from '../api/schemas/OrderSummarySchema';
import { OrderSchema } from '../api/schemas/OrderSchema';
import { orderStatuses } from '../components/OrderStatus/util/constants';
import toastr from 'toastr';

// get all orders
export const useOrders = (statuses: string) => {
  const getOrderSummaries = async (statuses: string) => {
    const response = await baseFetch(
      'orders',
      '/order_summaries',
      OrderSummarySchema,
      {
        searchParams: {
          statuses: statuses,
        },
      },
    ).then(res => res.orders);

    return response;
  };

  return useQuery(
    ['getOrderSummaries', statuses],
    () => getOrderSummaries(statuses),
    {
      onError: () => {
        toastr.options.closeButton = true;
        toastr.error('Error fetching orders.');
      },
    },
  );
};

// get tab orders
export const useTabOrders = (
  status: number,
  options?: Omit<
    UseInfiniteQueryOptions<OrderSummaryResponse>,
    'queryKey' | 'queryFn' | 'getNextPageParam'
  >,
) => {
  // TODO early return if filter is active?
  const getPaginatedTabOrders = async (
    status: number,
    { pageParam = { limit: 100, offset: 0 } },
  ) => {
    const response = await baseFetch(
      'internal',
      '/api/v2/load_more_order_summaries',
      OrderSummarySchema,
      {
        searchParams: {
          status: status.toString(),
          limit: pageParam.limit.toString(),
          offset: pageParam.offset.toString(),
        },
      },
    ).then(res => res.orders);

    return response;
  };

  return useInfiniteQuery<OrderSummaryResponse>(
    ['getTabOrders', status],
    // @ts-ignore
    ({ pageParam }) => getPaginatedTabOrders(status, { pageParam }),
    {
      ...options,
      getNextPageParam: (_, allPages) => {
        const fetchedRows = allPages.flatMap(page => page);
        return {
          limit: 100,
          offset: fetchedRows.length,
        };
      },
      onError: () => {
        toastr.options.closeButton = true;
        toastr.error('Error fetching tab orders.');
      },
    },
  );
};

// get filtered orders
export const useFilteredOrders = (
  filterText?: string,
  filterField?: string,
) => {
  const getFilteredOrders = async (
    filterText?: string,
    filterField?: string,
  ) => {
    if (!filterText && !filterField) {
      return;
    }

    const response = await baseFetch(
      'internal',
      '/api/v2/search_order_summaries',
      OrderSummarySchema,
      {
        searchParams: {
          field: filterField ?? '',
          value: filterText ?? '',
        },
      },
    ).then(res => res.orders);
    return response;
  };

  return useQuery(
    ['getFilteredOrders', filterText, filterField],
    () => getFilteredOrders(filterText, filterField),
    {
      enabled: !!filterText && !!filterField,
      onError: () => {
        toastr.options.closeButton = true;
        toastr.error('Error fetching filtered orders.');
      },
    },
  );
};

// get one order
export const useOrder = (uuid?: string) => {
  const getOrderByUuid = async (uuid?: string) => {
    if (!uuid) {
      return;
    }
    const response = await baseFetch('orders', `/${uuid}`, OrderSchema).then(
      res => res.order,
    );

    return response;
  };

  return useQuery(['getOrderByUuid', uuid], () => getOrderByUuid(uuid), {
    enabled: !!uuid?.length,
  });
};

export const useOrderSentryStatus = (orderId: number) => {
  const getOrderSentryStatus = async (orderId: number) => {
    const response = await baseFetch(
      'customer-farm-api',
      `/sentry_status/solar_order/${orderId}`,
      yup.boolean().defined(),
    );

    return response;
  };

  return useQuery(
    ['getOrderSentryStatus', orderId],
    () => getOrderSentryStatus(orderId),
    {
      onError: () => {
        toastr.options.closeButton = true;
        toastr.error('Error fetching order sentry status.');
      },
    },
  );
};

const useInvalidateOrderSentryStatus = (orderId: number) => {
  const queryClient = useQueryClient();
  return {
    invalidateOrderSentryStatus: () =>
      queryClient.invalidateQueries(['getOrderSentryStatus', orderId]),
  };
};

export const useUpdateOrderSentryStatus = (orderId: number) => {
  const { invalidateOrderSentryStatus } =
    useInvalidateOrderSentryStatus(orderId);

  return useMutation(
    ['updateOrderSentryStatus'],
    ({ newStatus }: { newStatus: boolean }) => {
      return baseFetch(
        'customer-farm-api',
        `/sentry_status/solar_order/${orderId}`,
        yup.boolean().nullable(),
        {
          method: 'PUT',
          requestBody: { is_sentry: newStatus },
        },
      );
    },
    {
      onSuccess: () => invalidateOrderSentryStatus(),
      onError: () => {
        toastr.options.closeButton = true;
        toastr.error('Error updating order sentry status.');
      },
    },
  );
};

export const useUpdateOrderImageSkipRate = (orderId: number) => {
  return useMutation(
    ['updateOrderImageSkipRate'],
    ({ newImageSkipRate }: { newImageSkipRate: number | null }) => {
      return baseFetch(
        'orders',
        `/${orderId}/image_skip_rate`,
        yup
          .object({
            exit_status: yup.number().defined(),
            image_skip_rate: yup.number().defined().nullable(),
          })
          .defined(),
        {
          method: 'PATCH',
          requestBody: { image_skip_rate: newImageSkipRate },
        },
      );
    },
    {
      onError: () => {
        toastr.options.closeButton = true;
        toastr.error('Error updating order image skip rate.');
      },
    },
  );
};

// invalidate one order
export const useInvalidateOrder = (uuid?: string) => {
  const queryClient = useQueryClient();

  return {
    invalidateOrder: () =>
      queryClient.invalidateQueries(['getOrderByUuid', uuid]),
  };
};

// invalidate all orders
export const useInvalidateOrders = () => {
  const queryClient = useQueryClient();

  return {
    invalidateOrders: () => queryClient.invalidateQueries('getOrderSummaries'),
  };
};

// invalidate tab orders
export const useInvalidateTabOrders = (status: number) => {
  const queryClient = useQueryClient();

  return {
    invalidateTabOrders: () =>
      queryClient.invalidateQueries(['getTabOrders', status]),
  };
};

// invalidate filtered orders
export const useInvalidateFilteredOrders = () => {
  const queryClient = useQueryClient();

  return {
    invalidateFilteredOrders: () =>
      queryClient.invalidateQueries(['getFilteredOrders']),
  };
};

export const useInvalidateAllOrders = (uuid?: string) => {
  const { invalidateOrder } = useInvalidateOrder(uuid);
  const { invalidateOrders } = useInvalidateOrders();
  const { invalidateFilteredOrders } = useInvalidateFilteredOrders();
  const { invalidateTabOrders: invalidateCancelledTabOrders } =
    useInvalidateTabOrders(orderStatuses.CANCELLED);
  const { invalidateTabOrders: invalidateStartedTabOrders } =
    useInvalidateTabOrders(orderStatuses.STARTED);
  const { invalidateTabOrders: invalidateDeliveredTabOrders } =
    useInvalidateTabOrders(orderStatuses.DELIVERED);

  return {
    invalidateAllOrders: async () => {
      await Promise.all([
        invalidateOrder(),
        invalidateOrders(),
        invalidateFilteredOrders(),
        invalidateCancelledTabOrders(),
        invalidateStartedTabOrders(),
        invalidateDeliveredTabOrders(),
      ]);
    },
  };
};
